/**
 * Assumption Nodes - Adjustable Fairy Farm Parameters
 *
 * These values can be adjusted to explore different scenarios
 * for your inherited fairy dust farm.
 */

import { assumption, AssumptionNode } from '../../lib/computation-graph';

export const assumptions: Record<string, AssumptionNode> = {
  // Farm Valuation
  farmValue: assumption('farmValue', 50000, {
    label: 'Farm Value',
    description: 'Current market value of the fairy dust farm in gold pieces',
    unit: 'currency',
    range: { min: 20000, max: 150000, step: 1000 },
    presets: {
      modest: {
        value: 30000,
        label: 'Modest (30K GP)',
        description: 'Small cottage farm near the Whispering Woods',
        citation: { source: 'Enchanted Properties Gazette' },
      },
      average: {
        value: 50000,
        label: 'Average (50K GP)',
        description: 'Standard farm with meadow and moonwell access',
        citation: { source: 'Fairy Realtor Association' },
      },
      premium: {
        value: 80000,
        label: 'Premium (80K GP)',
        description: 'Prime location near ancient fairy rings',
        citation: { source: 'Luxury Magical Estates' },
      },
      legendary: {
        value: 120000,
        label: 'Legendary (120K GP)',
        description: 'Historic farm with blessed grounds',
        citation: { source: 'Heritage Magical Properties' },
      },
    },
  }),

  // Fairy Population
  fairyCount: assumption('fairyCount', 24, {
    label: 'Fairy Population',
    description: 'Number of dust-producing fairies on the farm',
    unit: 'number',
    range: { min: 5, max: 100, step: 1 },
    presets: {
      small: {
        value: 12,
        label: 'Small Colony (12)',
        description: 'Cozy family of fairies, easy to manage',
        citation: { source: 'Fairy Husbandry Manual' },
      },
      medium: {
        value: 24,
        label: 'Medium Colony (24)',
        description: 'Standard commercial operation',
        citation: { source: 'Commercial Fairy Farming Guide' },
      },
      large: {
        value: 48,
        label: 'Large Colony (48)',
        description: 'Requires dedicated elf staff',
        citation: { source: 'Industrial Fairy Operations' },
      },
      massive: {
        value: 72,
        label: 'Massive Colony (72)',
        description: 'Major operation, complex logistics',
        citation: { source: 'Mega Fairy Farm Quarterly' },
      },
    },
  }),

  // Dust Production
  dustPerFairy: assumption('dustPerFairy', 0.5, {
    label: 'Dust Per Fairy',
    description: 'Ounces of fairy dust produced per fairy per month',
    unit: 'number',
    range: { min: 0.1, max: 2, step: 0.1 },
    presets: {
      low: {
        value: 0.3,
        label: 'Low (0.3 oz)',
        description: 'Young fairies or poor moonlight conditions',
        citation: { source: 'Fairy Productivity Studies' },
      },
      average: {
        value: 0.5,
        label: 'Average (0.5 oz)',
        description: 'Healthy fairies with good flower access',
        citation: { source: 'Dust Production Standards' },
      },
      high: {
        value: 0.8,
        label: 'High (0.8 oz)',
        description: 'Prime conditions, happy fairy workers',
        citation: { source: 'Optimal Fairy Care Research' },
      },
      exceptional: {
        value: 1.2,
        label: 'Exceptional (1.2 oz)',
        description: 'Ancient fairy lineage, enchanted grounds',
        citation: { source: 'Legendary Dust Producers' },
      },
    },
  }),

  // Dust Pricing
  dustPrice: assumption('dustPrice', 100, {
    label: 'Dust Price',
    description: 'Gold pieces per ounce of fairy dust',
    unit: 'currency',
    range: { min: 50, max: 300, step: 5 },
    presets: {
      wholesale: {
        value: 75,
        label: 'Wholesale (75 GP)',
        description: 'Bulk sales to potion makers',
        citation: { source: 'Dust Commodity Exchange' },
      },
      retail: {
        value: 100,
        label: 'Retail (100 GP)',
        description: 'Standard market rate',
        citation: { source: 'Magical Goods Price Index' },
      },
      premium: {
        value: 150,
        label: 'Premium (150 GP)',
        description: 'Certified organic, ethically sourced',
        citation: { source: 'Artisan Dust Markets' },
      },
      luxury: {
        value: 220,
        label: 'Luxury (220 GP)',
        description: 'Royal grade, full moon harvested',
        citation: { source: 'Royal Dust Suppliers Guild' },
      },
    },
  }),

  // Fairy Care Costs
  nectarCostPerFairy: assumption('nectarCostPerFairy', 8, {
    label: 'Nectar Cost Per Fairy',
    description: 'Monthly nectar and flower budget per fairy (gold pieces)',
    unit: 'currency',
    range: { min: 2, max: 25, step: 1 },
    presets: {
      basic: {
        value: 5,
        label: 'Basic (5 GP)',
        description: 'Standard wildflower nectar',
        citation: { source: 'Fairy Nutrition Guide' },
      },
      quality: {
        value: 8,
        label: 'Quality (8 GP)',
        description: 'Mixed garden nectar blend',
        citation: { source: 'Happy Fairy Handbook' },
      },
      premium: {
        value: 15,
        label: 'Premium (15 GP)',
        description: 'Rare orchid and moonflower nectar',
        citation: { source: 'Gourmet Fairy Feeding' },
      },
      royal: {
        value: 22,
        label: 'Royal (22 GP)',
        description: 'Unicorn-blessed starflower essence',
        citation: { source: 'Royal Fairy Care Standards' },
      },
    },
  }),

  // Housing Maintenance
  housingCost: assumption('housingCost', 200, {
    label: 'Monthly Housing Costs',
    description: 'Mushroom home repairs and fairy circle maintenance',
    unit: 'currency',
    range: { min: 50, max: 500, step: 25 },
    presets: {
      rustic: {
        value: 100,
        label: 'Rustic (100 GP)',
        description: 'Basic toadstool maintenance',
        citation: { source: 'Budget Fairy Housing' },
      },
      comfortable: {
        value: 200,
        label: 'Comfortable (200 GP)',
        description: 'Well-maintained mushroom village',
        citation: { source: 'Standard Fairy Accommodations' },
      },
      luxury: {
        value: 350,
        label: 'Luxury (350 GP)',
        description: 'Crystal-adorned fairy mansions',
        citation: { source: 'Luxury Fairy Living' },
      },
    },
  }),

  // Sparkle Maintenance
  sparkleMaintenanceCost: assumption('sparkleMaintenanceCost', 50, {
    label: 'Sparkle Maintenance',
    description: 'Monthly cost for wing polishing and sparkle renewal',
    unit: 'currency',
    range: { min: 20, max: 150, step: 10 },
    presets: {
      basic: {
        value: 30,
        label: 'Basic (30 GP)',
        description: 'Self-service sparkle stations',
        citation: { source: 'DIY Fairy Care' },
      },
      standard: {
        value: 50,
        label: 'Standard (50 GP)',
        description: 'Weekly professional grooming',
        citation: { source: 'Fairy Wellness Association' },
      },
      deluxe: {
        value: 100,
        label: 'Deluxe (100 GP)',
        description: 'Daily spa treatments and wing massage',
        citation: { source: 'Pampered Fairy Services' },
      },
    },
  }),

  // Elf Labor
  elfWorkers: assumption('elfWorkers', 2, {
    label: 'Elf Workers',
    description: 'Number of elf farm hands employed',
    unit: 'number',
    range: { min: 0, max: 10, step: 1 },
    presets: {
      none: {
        value: 0,
        label: 'None (Self-run)',
        description: 'Owner does all the work',
        citation: { source: 'Solo Fairy Farming' },
      },
      minimal: {
        value: 1,
        label: 'Minimal (1 elf)',
        description: 'Part-time helper for harvests',
        citation: { source: 'Small Farm Operations' },
      },
      standard: {
        value: 2,
        label: 'Standard (2 elves)',
        description: 'Full coverage for medium farm',
        citation: { source: 'Commercial Farm Staffing' },
      },
      full: {
        value: 4,
        label: 'Full Staff (4 elves)',
        description: 'Complete operation management',
        citation: { source: 'Large Farm Management' },
      },
    },
  }),

  // Farm Appreciation
  farmAppreciation: assumption('farmAppreciation', 3, {
    label: 'Farm Appreciation Rate',
    description: 'Annual increase in farm value',
    unit: 'percent',
    range: { min: -5, max: 10, step: 0.5 },
    presets: {
      declining: {
        value: -2,
        label: 'Declining (-2%)',
        description: 'Fairy dust market oversupply',
        citation: { source: 'Magical Market Forecast' },
      },
      stable: {
        value: 1,
        label: 'Stable (1%)',
        description: 'Mature market conditions',
        citation: { source: 'Enchanted Real Estate Review' },
      },
      growing: {
        value: 3,
        label: 'Growing (3%)',
        description: 'Increasing demand for fairy products',
        citation: { source: 'Dust Industry Outlook' },
      },
      booming: {
        value: 6,
        label: 'Booming (6%)',
        description: 'Fairy dust renaissance',
        citation: { source: 'Magical Commodity Boom Report' },
      },
    },
  }),

  // Dust Price Growth
  dustPriceGrowth: assumption('dustPriceGrowth', 2, {
    label: 'Dust Price Growth',
    description: 'Annual increase in fairy dust prices',
    unit: 'percent',
    range: { min: -5, max: 10, step: 0.5 },
    presets: {
      deflation: {
        value: -3,
        label: 'Deflation (-3%)',
        description: 'Synthetic dust competition',
        citation: { source: 'Alchemist Guild Reports' },
      },
      stable: {
        value: 0,
        label: 'Stable (0%)',
        description: 'Balanced supply and demand',
        citation: { source: 'Dust Price Stability Council' },
      },
      moderate: {
        value: 2,
        label: 'Moderate (2%)',
        description: 'Healthy market growth',
        citation: { source: 'Magical Economics Institute' },
      },
      high: {
        value: 5,
        label: 'High (5%)',
        description: 'Growing potion industry demand',
        citation: { source: 'Potion Makers Association' },
      },
    },
  }),

  // Alternative Investments
  wizardIndexReturns: assumption('wizardIndexReturns', 7, {
    label: 'Wizard Index Returns',
    description: 'Expected annual return on the Wizard 500 Index Fund',
    unit: 'percent',
    range: { min: 2, max: 15, step: 0.5 },
    presets: {
      conservative: {
        value: 4,
        label: 'Conservative (4%)',
        description: 'Post-war recovery scenario',
        citation: { source: 'Wizard Financial Advisors' },
      },
      historical: {
        value: 7,
        label: 'Historical (7%)',
        description: 'Long-term magical market average',
        citation: { source: 'Arcane Stock Exchange Data' },
      },
      optimistic: {
        value: 10,
        label: 'Optimistic (10%)',
        description: 'Magic boom continues',
        citation: { source: 'Bullish Wizard Quarterly' },
      },
      aggressive: {
        value: 13,
        label: 'Aggressive (13%)',
        description: 'New spell discovery era',
        citation: { source: 'Speculative Magic Investments' },
      },
    },
  }),

  dragonBondReturns: assumption('dragonBondReturns', 4.5, {
    label: 'Dragon Bond Returns',
    description: 'Annual return on Dragon Treasury Bonds',
    unit: 'percent',
    range: { min: 1, max: 8, step: 0.25 },
    presets: {
      low: {
        value: 2.5,
        label: 'Low (2.5%)',
        description: 'Dragons hoarding less',
        citation: { source: 'Dragon Treasury Weekly' },
      },
      current: {
        value: 4.5,
        label: 'Current (4.5%)',
        description: 'Current dragon hoard rates',
        citation: { source: 'Dragon Bond Rate Sheet' },
      },
      high: {
        value: 6,
        label: 'High (6%)',
        description: 'Dragon prosperity era',
        citation: { source: 'Golden Age Forecast' },
      },
    },
  }),

  // Selling Costs
  farmSellingCosts: assumption('farmSellingCosts', 8, {
    label: 'Farm Selling Costs',
    description: 'Gnome realtor fees and transfer taxes',
    unit: 'percent',
    range: { min: 3, max: 15, step: 0.5 },
    presets: {
      low: {
        value: 5,
        label: 'Low (5%)',
        description: 'Direct sale to known buyer',
        citation: { source: 'Private Sales Guide' },
      },
      standard: {
        value: 8,
        label: 'Standard (8%)',
        description: 'Full gnome realtor service',
        citation: { source: 'Gnome Realty Association' },
      },
      premium: {
        value: 12,
        label: 'Premium (12%)',
        description: 'Luxury farm marketing campaign',
        citation: { source: 'Elite Magical Properties' },
      },
    },
  }),
};

export default assumptions;
